// frontend/src/components/UserManagement.tsx
import React, { useState, useEffect } from 'react';
import { Container, Card, Table, Button, Badge, Modal, Form, Alert, Accordion, Row, Col } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import { User, Role, RolePermissions } from '../interfaces';
import { useAuth } from '../contexts/AuthContext';
import MessageModal from './MessageModal';
import ConfirmationModal from './ConfirmationModal';

import { API_BASE_URL, buildApiUrl } from '../config/api';

const UserManagement: React.FC = () => {
  const { canManageUsers, user: currentUser } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [pendingUsers, setPendingUsers] = useState<User[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [rolePermissions, setRolePermissions] = useState<RolePermissions[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [selectedRole, setSelectedRole] = useState<number | null>(null);
  const [stats, setStats] = useState<any>(null);
  
  // מודל הודעות
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [messageTitle, setMessageTitle] = useState('');
  const [messageContent, setMessageContent] = useState('');
  const [messageVariant, setMessageVariant] = useState<'success' | 'danger' | 'info' | 'warning'>('info');
  
  // מודל אישור
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmTitle, setConfirmTitle] = useState('');
  const [confirmMessage, setConfirmMessage] = useState('');
  const [confirmAction, setConfirmAction] = useState<() => void>(() => {});

  useEffect(() => {
    if (canManageUsers) {
      fetchData();
    }
  }, [canManageUsers]);

  const showMessage = (title: string, message: string, variant: 'success' | 'danger' | 'info' | 'warning') => {
    setMessageTitle(title);
    setMessageContent(message);
    setMessageVariant(variant);
    setShowMessageModal(true);
  };

  const fetchData = async () => {
    setLoading(true);
    setError('');

    try {
      const [usersRes, pendingRes, rolesRes, rolesDescRes, statsRes] = await Promise.all([
        axios.get<User[]>(buildApiUrl('users/users/')),
        axios.get<User[]>(buildApiUrl('users/users/pending_users/')),
        axios.get<Role[]>(buildApiUrl('users/roles/')),
        axios.get<RolePermissions[]>(buildApiUrl('users/roles/descriptions/')),
        axios.get(buildApiUrl('users/users/stats/'))
      ]);

      setUsers(usersRes.data);
      setPendingUsers(pendingRes.data);
      setRoles(rolesRes.data);
      setRolePermissions(rolesDescRes.data);
      setStats(statsRes.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'שגיאה בטעינת נתונים');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAllRoles = async () => {
    try {
      const response = await axios.post(buildApiUrl('create-all-roles/?force=true'));
      showMessage('הצלחה', response.data.message, 'success');
      fetchData(); // רענון הנתונים
    } catch (err: any) {
      showMessage('שגיאה', err.response?.data?.error || err.message, 'danger');
    }
  };

  const handleApproveUser = async () => {
    if (!selectedUser || !selectedRole) {
      showMessage('שגיאה', 'יש לבחור תפקיד למשתמש', 'danger');
      return;
    }

    try {
      await axios.post(`${buildApiUrl('users/users/')}${selectedUser.id}/approve_user/`, {
        is_approved: true,
        role: selectedRole
      });

      showMessage('הצלחה', `המשתמש ${selectedUser.username} אושר בהצלחה`, 'success');
      
      setShowApprovalModal(false);
      setSelectedUser(null);
      setSelectedRole(null);
      fetchData();
    } catch (err: any) {
      showMessage('שגיאה', err.response?.data?.detail || 'שגיאה באישור משתמש', 'danger');
    }
  };

  const handleRejectUser = async (userId: number) => {
    if (!window.confirm('האם אתה בטוח שברצונך לדחות את המשתמש?')) {
      return;
    }

    try {
      await axios.post(`${buildApiUrl('users/users/')}${userId}/reject_user/`);
      showMessage('הצלחה', 'המשתמש נדחה בהצלחה', 'success');
      fetchData();
    } catch (err: any) {
      showMessage('שגיאה', err.response?.data?.detail || 'שגיאה בדחיית משתמש', 'danger');
    }
  };

  const handleUpdateUserRole = async (userId: number, roleId: number) => {
    try {
      // עדכון מיידי בצד הלקוח
      const newRole = roles.find(r => r.id === roleId);
      setUsers(prevUsers => 
        prevUsers.map(u => 
          u.id === userId ? { ...u, role: newRole } : u
        )
      );
      
      await axios.patch(`${buildApiUrl('users/users/')}${userId}/`, {
        role_id: roleId
      });
      
      showMessage('הצלחה', 'תפקיד המשתמש עודכן בהצלחה', 'success');
      fetchData();
    } catch (err: any) {
      // במקרה של שגיאה, מחזירים את המצב הקודם
      fetchData();
      showMessage('שגיאה', err.response?.data?.detail || 'שגיאה בעדכון תפקיד', 'danger');
    }
  };

  const handleToggleUserActive = (userId: number, isActive: boolean) => {
    const user = users.find(u => u.id === userId);
    setConfirmTitle(isActive ? 'השבתת משתמש' : 'הפעלת משתמש');
    setConfirmMessage(`האם אתה בטוח שברצונך ${isActive ? 'להשבית' : 'להפעיל'} את המשתמש ${user?.username}?`);
    setConfirmAction(() => async () => {
      try {
        console.log('Toggling user active:', userId, 'current:', isActive, 'new:', !isActive);
        
        // שליחת הבקשה לשרת
        const response = await axios.patch(`${buildApiUrl('users/users/')}${userId}/`, {
          is_active: !isActive
        });
        
        console.log('Server response:', response.data);
        
        // עדכון מיידי בצד הלקוח עם התשובה מהשרת
        setUsers(prevUsers => 
          prevUsers.map(u => 
            u.id === userId ? { ...u, ...response.data } : u
          )
        );
        
        // הצגת הודעת הצלחה
        showMessage('הצלחה', `המשתמש ${user?.username} ${isActive ? 'הושבת' : 'הופעל'} בהצלחה`, 'success');
        
        // רענון מלא של הנתונים אחרי עיכוב קטן
        setTimeout(async () => {
          await fetchData();
        }, 500);
      } catch (err: any) {
        console.error('Error toggling user:', err);
        // במקרה של שגיאה, רענון הנתונים
        fetchData();
        showMessage('שגיאה', err.response?.data?.detail || 'שגיאה בעדכון סטטוס', 'danger');
      }
    });
    setShowConfirmModal(true);
  };

  const handleDeleteUser = (userId: number) => {
    const user = users.find(u => u.id === userId);
    setConfirmTitle('מחיקת משתמש');
    setConfirmMessage(`האם אתה בטוח שברצונך למחוק את המשתמש ${user?.username}? פעולה זו לא ניתנת לביטול!`);
    setConfirmAction(() => async () => {
      try {
        await axios.delete(`${buildApiUrl('users/users/')}${userId}/delete_user/`);
        
        showMessage('הצלחה', `המשתמש ${user?.username} נמחק בהצלחה מהמערכת`, 'success');
        
        // רענון הנתונים
        await fetchData();
      } catch (err: any) {
        showMessage('שגיאה', err.response?.data?.error || err.response?.data?.detail || 'שגיאה במחיקת משתמש', 'danger');
      }
    });
    setShowConfirmModal(true);
  };

  const getStatusBadge = (user: User) => {
    if (!user.is_active) {
      return <Badge bg="secondary">לא פעיל</Badge>;
    } else if (!user.is_approved) {
      return <Badge bg="warning">ממתין לאישור</Badge>;
    } else {
      return <Badge bg="success">פעיל</Badge>;
    }
  };

  const getRolePermissionsDescription = (roleName: string) => {
    const roleInfo = rolePermissions.find(r => r.name === roleName);
    return roleInfo?.permissions || '';
  };

  if (!canManageUsers) {
    return (
      <Container className="mt-5" style={{ direction: 'rtl' }}>
        <Alert variant="danger">אין לך הרשאה לצפות בעמוד זה!</Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container className="mt-5 text-center" style={{ direction: 'rtl' }}>
        <p>טוען...</p>
      </Container>
    );
  }

  return (
    <Container fluid className="mt-4" style={{ direction: 'rtl' }}>
      <div className="d-flex justify-content-between align-items-center mb-4 app-sticky-page-toolbar">
        <h2 className="mb-0">ניהול משתמשים</h2>
        {roles.length < 5 && (
          <Button variant="warning" onClick={handleCreateAllRoles}>
            יצירת כל התפקידים ({roles.length}/5)
          </Button>
        )}
      </div>

      {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}

      {/* סטטיסטיקות */}
      {stats && (
        <Row className="mb-4">
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <h4>{stats.total}</h4>
                <p className="text-muted">סה"כ משתמשים</p>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <h4 className="text-success">{stats.approved}</h4>
                <p className="text-muted">משתמשים מאושרים</p>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <h4 className="text-warning">{stats.pending}</h4>
                <p className="text-muted">ממתינים לאישור</p>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* פירוט הרשאות לכל תפקיד */}
      <Card className="mb-4">
        <Card.Header as="h5">רמות הרשאה והגדרותיהן</Card.Header>
        <Card.Body>
          <Accordion>
            {rolePermissions.map((role, index) => (
              <Accordion.Item eventKey={index.toString()} key={role.id}>
                <Accordion.Header>
                  <strong>{role.display_name}</strong>
                </Accordion.Header>
                <Accordion.Body>
                  <p className="mb-2">{role.permissions}</p>
                  <hr />
                  <Row>
                    <Col md={4}>
                      <small className="text-muted">עמוד ברירת מחדל:</small> {role.default_page}
                    </Col>
                    <Col md={8}>
                      <small>
                        <strong>הרשאות:</strong>{' '}
                        {role.can_manage_users && <Badge bg="primary" className="me-1">ניהול משתמשים</Badge>}
                        {role.can_delete && <Badge bg="danger" className="me-1">מחיקה</Badge>}
                        {role.can_edit_equipment && <Badge bg="info" className="me-1">עריכת ציוד</Badge>}
                        {role.can_edit_events && <Badge bg="warning" className="me-1">עריכת אירועים</Badge>}
                        {role.can_only_view && <Badge bg="secondary" className="me-1">צפייה בלבד</Badge>}
                      </small>
                    </Col>
                  </Row>
                </Accordion.Body>
              </Accordion.Item>
            ))}
          </Accordion>
        </Card.Body>
      </Card>

      {/* משתמשים ממתינים לאישור */}
      {pendingUsers.length > 0 && (
        <Card className="mb-4 border-warning">
          <Card.Header className="bg-warning text-dark" as="h5">
            משתמשים ממתינים לאישור ({pendingUsers.length})
          </Card.Header>
          <Card.Body>
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>שם משתמש</th>
                  <th>אימייל</th>
                  <th>שם מלא</th>
                  <th>טלפון</th>
                  <th>תאריך הרשמה</th>
                  <th>שיטת התחברות</th>
                  <th>פעולות</th>
                </tr>
              </thead>
              <tbody>
                {pendingUsers.map((user) => (
                  <tr key={user.id}>
                    <td>{user.username}</td>
                    <td>{user.email}</td>
                    <td>{user.full_name || '-'}</td>
                    <td>{user.phone_number || '-'}</td>
                    <td>{user.created_at ? new Date(user.created_at).toLocaleDateString('he-IL') : '-'}</td>
                    <td>
                      {user.auth_provider === 'google' ? (
                        <Badge bg="danger">Google</Badge>
                      ) : (
                        <Badge bg="primary">מקומי</Badge>
                      )}
                    </td>
                    <td>
                      <Button
                        size="sm"
                        variant="success"
                        className="me-2"
                        onClick={() => {
                          setSelectedUser(user);
                          setShowApprovalModal(true);
                        }}
                      >
                        אשר
                      </Button>
                      <Button
                        size="sm"
                        variant="danger"
                        onClick={() => handleRejectUser(user.id)}
                      >
                        דחה
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}

      {/* כל המשתמשים */}
      <Card>
        <Card.Header as="h5">כל המשתמשים</Card.Header>
        <Card.Body>
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th>שם משתמש</th>
                <th>אימייל</th>
                <th>שם מלא</th>
                <th>תפקיד</th>
                <th>סטטוס</th>
                <th>שיטת התחברות</th>
                <th>אושר על ידי</th>
                <th>פעולות</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id}>
                  <td>{user.username}</td>
                  <td>{user.email}</td>
                  <td>{user.full_name || '-'}</td>
                  <td>
                    {user.is_approved ? (
                      <Form.Select
                        size="sm"
                        value={user.role?.id || ''}
                        onChange={(e) => handleUpdateUserRole(user.id, parseInt(e.target.value))}
                      >
                        <option value="">בחר תפקיד</option>
                        {roles.map((role) => (
                          <option key={role.id} value={role.id}>
                            {role.display_name || role.name}
                          </option>
                        ))}
                      </Form.Select>
                    ) : (
                      <span className="text-muted">לא הוגדר</span>
                    )}
                  </td>
                  <td>{getStatusBadge(user)}</td>
                  <td>
                    {user.auth_provider === 'google' ? (
                      <Badge bg="danger">Google</Badge>
                    ) : (
                      <Badge bg="primary">מקומי</Badge>
                    )}
                  </td>
                  <td>{user.approved_by_name || '-'}</td>
                  <td>
                    <div className="d-flex gap-2">
                      <Button
                        size="sm"
                        variant={user.is_active ? 'warning' : 'success'}
                        onClick={() => handleToggleUserActive(user.id, user.is_active)}
                      >
                        {user.is_active ? 'השבת' : 'הפעל'}
                      </Button>
                      <Button
                        size="sm"
                        variant="danger"
                        onClick={() => handleDeleteUser(user.id)}
                        disabled={user.id === currentUser?.id} // מניעת מחיקת המשתמש הנוכחי
                        title={user.id === currentUser?.id ? 'לא ניתן למחוק את המשתמש הנוכחי' : 'מחק משתמש'}
                      >
                        <FontAwesomeIcon icon={faTrash} className="me-1" />
                        מחק
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      {/* Modal לאישור משתמש */}
      <Modal show={showApprovalModal} onHide={() => setShowApprovalModal(false)} dir="rtl">
        <Modal.Header closeButton>
          <Modal.Title>אישור משתמש</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedUser && (
            <>
              <p>
                <strong>שם משתמש:</strong> {selectedUser.username}
              </p>
              <p>
                <strong>אימייל:</strong> {selectedUser.email}
              </p>
              <p>
                <strong>שם מלא:</strong> {selectedUser.full_name || '-'}
              </p>

              <Form.Group className="mb-3">
                <Form.Label>בחר תפקיד *</Form.Label>
                <Form.Select
                  value={selectedRole || ''}
                  onChange={(e) => setSelectedRole(parseInt(e.target.value))}
                  required
                >
                  <option value="">בחר תפקיד</option>
                  {roles.map((role) => (
                    <option key={role.id} value={role.id}>
                      {role.display_name || role.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              {selectedRole && (
                <Alert variant="info">
                  <small>
                    <strong>הרשאות התפקיד:</strong><br />
                    {roles.find(r => r.id === selectedRole)?.name && 
                      getRolePermissionsDescription(roles.find(r => r.id === selectedRole)!.name)}
                  </small>
                </Alert>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowApprovalModal(false)}>
            ביטול
          </Button>
          <Button variant="primary" onClick={handleApproveUser} disabled={!selectedRole}>
            אשר משתמש
          </Button>
        </Modal.Footer>
      </Modal>

      {/* מודל הודעות */}
      <MessageModal
        show={showMessageModal}
        onHide={() => setShowMessageModal(false)}
        title={messageTitle}
        message={messageContent}
        variant={messageVariant}
      />

      {/* מודל אישור */}
      <ConfirmationModal
        show={showConfirmModal}
        onHide={() => setShowConfirmModal(false)}
        title={confirmTitle}
        message={confirmMessage}
        onConfirm={confirmAction}
        confirmText="אישור"
        cancelText="ביטול"
        confirmVariant="warning"
      />
    </Container>
  );
};

export default UserManagement;
